import React from 'react';
import clsx from 'clsx';
import {
  ARCHIVED_POLICY_STATUS_COLOR,
  CANCELLED_PAYMENT_STATUS_COLOR,
  COMPREHENSIVE_100_COLOR,
  COMPREHENSIVE_85_COLOR,
  COMPREHENSIVE_90_COLOR,
  DECLINED_PAYMENT_STATUS_COLOR,
  DRAFT,
  DUE_LATER_POLICY_STATUS_COLOR,
  EXISTING_POLICY_STATUS_COLOR,
  EXPIRED_POLICY_STATUS_COLOR,
  IN_APPROVAL_PAYMENT_STATUS_COLOR,
  IN_REVIEW_PAYMENT_STATUS_COLOR,
  NEW,
  ON_HOLD_PAYMENT_STATUS_COLOR,
  OVERDUE_STATUS_COLOR,
  SMART_DRIVE_COLOR,
  SUCCESS_PAYMENT_STATUS_COLOR,
  THIRD_PARTY_COLOR,
  THIRD_PARTY_THEFT_COLOR,
} from '../../resources/constants';
import { capitalizeSentence } from '../../utils/miscFunctions';
import {
  FUEL_SALES_STATUS,
  LEAD_STATUS,
  POLICY_REVIEW_STATES,
  TASK_EVENT,
  TASK_STATUSES
} from '../../resources/enums';
import { CheckCircle, PlusCircle, Clock8, Search, PauseCircle, CalendarClock, Hourglass, XCircle, AlarmClockOff, Ban, Archive, FileSearch, BellPlus, Moon, Crown, Star, Gem, FileClock, ShieldX, Newspaper, AlertTriangle } from "lucide-react";

const PENDING_REQUIREMENTS_LABEL = 'Pending Requirements';

interface IPolicyStatusProps {
  status: string | null | boolean;
  classes?: string;
  firstSuccessfulPayment?: boolean;
  /**
   * Bulk-imported (historical) policies show their "New" review stage as
   * "Existing" instead, since the data was uploaded rather than freshly created.
   * Only the visible label changes — the icon/colour still key off "New".
   */
  bulkImported?: boolean | null;
}

const PolicyStatus: React.FC<IPolicyStatusProps> = ({ status: statusProp, classes, firstSuccessfulPayment, bulkImported }) => {

  // A NEW/DRAFT policy that has already taken its first payment is "Pending
  // Approval" per the spec, not "Draft"/"New". getPolicyReviewStatus maps the
  // raw reviewStage and is payment-blind, so the list badge would otherwise
  // read "Draft" while the detail page (getStatusLabel) and the edit-form
  // field locks both treat it as Pending Approval. Normalise here — the one
  // place that already knows whether the first payment landed — so every
  // caller passing firstSuccessfulPayment stays consistent. Non-policy
  // callers (tasks, leads, fuel) never pass firstSuccessfulPayment, so their
  // "New" status is untouched.
  const status =
    firstSuccessfulPayment === true && (statusProp === 'Draft' || statusProp === 'New')
      ? 'Pending Approval'
      : statusProp;

  // Bulk-imported (historical) policies relabel the "Draft"/"New" review
  // stage as "Existing" and get their own muted slate colour. Callers
  // upstream may either (a) pass the raw stage label ("New") along with
  // bulkImported=true (legacy call sites), or (b) pass "Existing" directly
  // because Policy.getStatusLabel() already applied the swap. Recognise
  // both so the styling always fires.
  const isExistingBulk =
    status === 'Existing' || (bulkImported === true && status === 'New');

  const getStatusIcon = (status: string | null | boolean) => {
    if (
      status === POLICY_REVIEW_STATES.APPROVED ||
      status === 'Approved' ||
      // Policy.getStatusLabel() maps APPROVED → 'Active' per the spec,
      // so upstream callers now pass 'Active' as the status. Recognise
      // it alongside 'Approved' so the pill picks up the checkmark
      // icon and colour instead of rendering as bare text.
      status === 'Active' ||
      status === TASK_EVENT.POLICY ||
      status === TASK_STATUSES.DONE ||
      status === 'active' ||
      status === capitalizeSentence(LEAD_STATUS.PAID) ||
      status === TASK_EVENT.QUOTE ||
      status === FUEL_SALES_STATUS.COMPLETED ||
      status === true
    ) {
      return <CheckCircle className="inline-block w-4 h-4 mr-1" />;
    }
    if (status === 'Draft') {
      return <Newspaper className="inline-block w-4 h-4 mr-1" />;
    }

    if (
      status === 'New' ||
      status === POLICY_REVIEW_STATES.NEW ||
      status === TASK_EVENT.LEAD ||
      status === TASK_STATUSES.NEW
    ) {
      return <BellPlus className="inline-block w-4 h-4 mr-1" />;
    }

    if (
      status === 'In Approval' ||
      status === POLICY_REVIEW_STATES.IN_APPROVAL ||
      status === 'Pending Approval' ||
      status === capitalizeSentence(LEAD_STATUS.WARM) ||
      status === TASK_EVENT.CUSTOMER ||
      status === TASK_STATUSES.INAPPROVAL ||
      status === FUEL_SALES_STATUS.PENDING_CLOSE ||
      status === 'PENDING_CLOSE'
    ) {
      return <Clock8 className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === PENDING_REQUIREMENTS_LABEL) {
      return <AlertTriangle className="inline-block w-4 h-4 mr-1" />;
    }

    if (
      status === POLICY_REVIEW_STATES.IN_REVIEW ||
      status === 'In Review' ||
      status === TASK_EVENT.COMMUNITY ||
      status === TASK_STATUSES.IN_PROGRESS ||
      (firstSuccessfulPayment && (status === 'Due Later' || status === TASK_EVENT.PAYMENT))
    ) {
      return <FileClock className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === POLICY_REVIEW_STATES.ON_HOLD || status === 'On Hold' ||
      status === capitalizeSentence(LEAD_STATUS.COLD)) {
      return <PauseCircle className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === POLICY_REVIEW_STATES.DECLINED || status === 'Declined') {
      return <XCircle className="inline-block w-4 h-4 mr-1" />;
    }

    if (
      status === POLICY_REVIEW_STATES.CANCELLED ||
      status === 'Cancelled' ||
      // Policy.getStatusLabel() returns the US-spelled 'Canceled'
      // for the CANCELLED review stage. Match it here so the pill
      // picks up the XCircle icon instead of rendering as bare text.
      status === 'Canceled' ||
      status === capitalizeSentence(LEAD_STATUS.CLOSED)
    ) {
      return <XCircle className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === POLICY_REVIEW_STATES.ARCHIVED || status === 'Archived') {
      return <Archive className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === POLICY_REVIEW_STATES.DUE_LATER || status === 'Due Later') {
      return <CalendarClock className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === POLICY_REVIEW_STATES.EXPIRED || status === 'Expired' || status === false) {
      return <ShieldX className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === TASK_STATUSES.OVERDUE) {
      return <AlarmClockOff className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === '100% Comprehensive' || status === '90% Comprehensive' || status === '85% Comprehensive') {
      return <Crown className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === 'Third Party (Fire/Theft)') {
      return <Gem className="inline-block w-4 h-4 mr-1" />;
    }

    if (status === 'Third Party') {
      return <Star className="inline-block w-4 h-4 mr-1" />;
    }

    return null;
  };

  const statusClass = clsx(
    '!w-fit  whitespace-nowrap  h-[24px] !rounded-full !text-xs gap-[2px] flex items-center justify-start',
    {
      [COMPREHENSIVE_100_COLOR]:
        status === '100% Comprehensive',
      [COMPREHENSIVE_90_COLOR]:
        status === '90% Comprehensive',
      [THIRD_PARTY_COLOR]:
        status === 'Third Party',
      [THIRD_PARTY_THEFT_COLOR]:
        status === 'Third Party (Fire/Theft)',
      [COMPREHENSIVE_85_COLOR]:
        status === '85% Comprehensive',
      [SMART_DRIVE_COLOR]:
        status === 'Smart Drive',
      [SUCCESS_PAYMENT_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.APPROVED ||
        status === 'Approved' ||
        // Match Policy.getStatusLabel()'s 'Active' output so approved
        // rows render with the green pill instead of unstyled text.
        status === 'Active' ||
        status === TASK_EVENT.POLICY ||
        status === TASK_STATUSES.DONE ||
        status === 'active' ||
        status === capitalizeSentence(LEAD_STATUS.PAID) ||
        status === TASK_EVENT.QUOTE ||
        status === FUEL_SALES_STATUS.COMPLETED ||
        status === true ||
        status === 'Ride Sharing',
      [NEW]:
        (status === 'New' && !isExistingBulk) ||
        status === POLICY_REVIEW_STATES.NEW ||
        status === TASK_EVENT.LEAD ||
        status === TASK_STATUSES.NEW ||
        status === 'Internal' ||
        status === 'Repair Center' ||
        status === 'Annually',

      [EXISTING_POLICY_STATUS_COLOR]: isExistingBulk,

      [DRAFT]:
        status === 'Draft',

      [IN_APPROVAL_PAYMENT_STATUS_COLOR]:
        status === 'In Approval' ||
        status === POLICY_REVIEW_STATES.IN_APPROVAL ||
        // "Pending Approval" is a synthetic spec label that the badge
        // never received styling for, so it rendered as bare text. It
        // shares the broker-action-needed semantics with In Approval,
        // so reuse the same amber/clock styling rather than introducing
        // a separate palette entry.
        status === 'Pending Approval' ||
        status === capitalizeSentence(LEAD_STATUS.WARM) ||
        status === TASK_EVENT.CUSTOMER ||
        status === TASK_STATUSES.INAPPROVAL ||
        status === FUEL_SALES_STATUS.PENDING_CLOSE ||
        status === 'PENDING_CLOSE' ||
        status === 'Insurer' ||
        status === 'Monthly',

      [OVERDUE_STATUS_COLOR]:
        status === TASK_STATUSES.OVERDUE ||
        status === 'Weekly',

      [IN_REVIEW_PAYMENT_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.IN_REVIEW ||
        status === 'In Review' ||
        status === capitalizeSentence(LEAD_STATUS.CLOSED) ||
        status === TASK_EVENT.COMMUNITY ||
        status === TASK_STATUSES.IN_PROGRESS ||
        (firstSuccessfulPayment && (status === 'Due Later' || status === TASK_EVENT.PAYMENT)) ||
        status === 'Broker' ||
        status === 'Daily',

      [ON_HOLD_PAYMENT_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.ON_HOLD || status === 'On Hold' ||
        status === 'Financier',

      [DECLINED_PAYMENT_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.DECLINED || status === 'Declined',

      [CANCELLED_PAYMENT_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.CANCELLED ||
        status === 'Cancelled' ||
        // Match Policy.getStatusLabel()'s 'Canceled' output so
        // cancelled rows render with the red pill instead of unstyled text.
        status === 'Canceled' ||
        status === capitalizeSentence(LEAD_STATUS.COLD),

      [ARCHIVED_POLICY_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.ARCHIVED || status === 'Archived' ||
        status === 'Fuel Provider',

      [DUE_LATER_POLICY_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.DUE_LATER || status === 'Due Later' ||
        status === 'Agency' ||
        status === 'Other',

      [EXPIRED_POLICY_STATUS_COLOR]:
        status === POLICY_REVIEW_STATES.EXPIRED || status === 'Expired' || status === false ||
        status === 'Fleet',
      ['!bg-amber-100 !text-amber-800 px-2']: status === PENDING_REQUIREMENTS_LABEL,
      ['bg-status-inactive']: status === 'inactive' || status === null
    },
    classes
  );

  const displayStatus =
    status === null
      ? '-'
      : status === 'Approved'
        ? 'Active'
        : isExistingBulk
          ? 'Existing'
          : status;

  return (
    <div className="w-full flex items-start">
      <div className={statusClass}><span>{getStatusIcon(status)}</span> <span>{displayStatus}</span></div>
    </div>
  );
};

export default PolicyStatus;
